import React, { useMemo } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import {
  RESERVATION_STATUS_LABEL,
  ReservationStatus,
  TimelineEntry,
  TimelineView,
} from '../../types/reservation';
import { seatCapacitySummary } from './Settings/SeatTypesTab';

/**
 * 台帳（席タイプ別タイムライン）。横=時間軸・縦=席タイプ別レーンで占有バーを表示する。
 * ライブラリは使わず CSS（絶対配置 + %幅）で描画する。
 * SEAT_TYPE モード: 席タイプごとのレーン + 未割当レーン。
 * AGGREGATE モード: seatTypes が空で返るため、全予約を単一レーンに表示する。
 */

interface TimelineBoardProps {
  timeline: TimelineView;
  onEntryClick: (reservationId: string) => void;
}

const ROW_HEIGHT = 36;
const LABEL_WIDTH = 130;

/** 'HH:mm'（または 'HH:mm:ss'）→ 分 */
const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map((v) => parseInt(v, 10));
  return h * 60 + (m || 0);
};

/**
 * 営業日が翌日にまたがる（深夜営業）かどうか判定する。
 * 各営業時間帯で close<=open のものが1つでもあれば「その営業日は翌0:00以降まで続く」とみなす。
 * これに該当する日は時間軸を24:00超（例27:00）まで拡張し、翌0:00〜の予約を連続表示する。
 */
const isOvernightBusinessDay = (hours: { open: string; close: string }[]): boolean =>
  hours.some((h) => toMinutes(h.close) <= toMinutes(h.open) && toMinutes(h.close) >= 0);

/**
 * ISO ローカル日時 → 台帳の時間軸上の分。
 * 通常営業日は当日の時刻をそのまま返し、翌日にまたがる終了は 24:00 でクランプする。
 * 深夜営業日（overnight=true）は翌日分を +1440（例 翌1:00 → 25:00）して連続した軸で描画する。
 */
const dateTimeToMinutes = (isoLocal: string, baseDate: string, overnight = false): number => {
  const time = isoLocal.length >= 16 ? isoLocal.substring(11, 16) : '00:00';
  const minutes = toMinutes(time);
  // 翌日にまたがる場合（日付部分が基準日より後）
  if (isoLocal.substring(0, 10) > baseDate) {
    // 深夜営業日は営業日基準で連続化（翌0:00=24:00）。通常営業日は従来どおり 24:00 でクランプ。
    return overnight ? minutes + 24 * 60 : 24 * 60;
  }
  return minutes;
};

/** 軸上の分 → 目盛ラベル。24:00以上は「翌0:00」「翌1:00」等の翌日表記にする。 */
const tickLabel = (minutes: number): string => {
  if (minutes >= 24 * 60) return `翌${Math.floor(minutes / 60) - 24}:00`;
  return `${Math.floor(minutes / 60)}:00`;
};

/** ステータス→バー色（theme のパレットに寄せた手動値。終端系は薄く表示） */
const barColor = (status: ReservationStatus): { bg: string; fg: string } => {
  switch (status) {
    case 'REQUESTED':
      return { bg: '#ff9800', fg: '#fff' };
    case 'CONFIRMED':
      return { bg: '#4caf50', fg: '#fff' };
    case 'COMPLETED':
      return { bg: '#2196f3', fg: '#fff' };
    case 'NO_SHOW':
      return { bg: '#f44336', fg: '#fff' };
    default:
      return { bg: '#bdbdbd', fg: '#fff' };
  }
};

/**
 * レーン内でエントリが重ならないよう貪欲法でトラック（段）を割り当てる。
 * 席の連結（units>1）予約は連続する units 本のトラックを占有する（占有卓数を台帳上でも正しく見せる）。
 * span=そのエントリが占有するトラック本数（=消費卓数・最小1）。
 *
 * seedTracks に席タイプの卓数(unitCount)を渡すと、その本数のトラックを最初に「空き」として確保する。
 * これにより、時間帯が重ならない予約は同じ卓を再利用でき、連結予約が確保済みの卓に収まる限り
 * オーバーブック行（unitCount 超過）を作らない。例: 20:00-22:00 の予約が卓1にあっても、
 * 22:00-24:00 の 2卓連結予約は卓1・卓2（どちらもその時刻は空き）に入り、卓3を作らない。
 */
const assignTracks = (
  entries: TimelineEntry[],
  baseDate: string,
  seedTracks = 0,
  overnight = false,
): { entry: TimelineEntry; track: number; span: number }[] => {
  const sorted = [...entries].sort(
    (a, b) =>
      dateTimeToMinutes(a.start, baseDate, overnight) - dateTimeToMinutes(b.start, baseDate, overnight),
  );
  // 各トラックの「次に空く時刻」。連結予約は連続する空きトラックをまとめて確保する。
  // 席タイプの卓数ぶんは最初から空き(0=どの時刻にも空いている)トラックとして用意しておく。
  const trackEnds: number[] = new Array(Math.max(0, seedTracks)).fill(0);
  return sorted.map((entry) => {
    const start = dateTimeToMinutes(entry.start, baseDate, overnight);
    const end = dateTimeToMinutes(entry.end, baseDate, overnight);
    const span = Math.max(1, entry.units ?? 1);
    // 連続する span 本が同時に空いている先頭トラックを探す
    let track = -1;
    for (let i = 0; i + span <= trackEnds.length; i += 1) {
      let ok = true;
      for (let j = 0; j < span; j += 1) {
        if (trackEnds[i + j] > start) {
          ok = false;
          break;
        }
      }
      if (ok) {
        track = i;
        break;
      }
    }
    if (track === -1) {
      // 既存トラックに収まらなければ末尾へ span 本ぶん追加する
      track = trackEnds.length;
      for (let j = 0; j < span; j += 1) trackEnds.push(end);
    } else {
      for (let j = 0; j < span; j += 1) trackEnds[track + j] = end;
    }
    return { entry, track, span };
  });
};

interface PlacedEntry {
  entry: TimelineEntry;
  track: number;
  span: number;
}

/**
 * 台帳の1レーン。
 * 席タイプ単位で「高さ = rowCount×ROW_HEIGHT」の1コンテナを作り、卓(unit)の区切りは
 * コンテナ内部に描く。各予約バーは絶対配置で top=track×ROW_HEIGHT / height=span×ROW_HEIGHT とし、
 * 連結(span>1)は複数卓行にまたがる1本の帯として描画する（重複描画はしない・タスク3）。
 * - kind='seatType': 席タイプのコンテナ。rowCount 本の卓行（区切り線＋卓ラベル）を内包する。unitCount 超は overbook 行。
 * - kind='unassigned': 未割当レーン（従来どおりトラック stacking で1コンテナに詰める）。
 * - kind='aggregate': AGGREGATE 店舗の単一レーン（従来どおりトラック stacking）。
 */
interface Lane {
  key: string;
  label: string;
  sublabel: string | null;
  /** このレーンに描画する配置済みエントリ（各エントリは1回だけ） */
  placed: PlacedEntry[];
  /** レーンの高さに使う行数（seatType=卓数含む rowCount、stacking=最大 track+span） */
  rowCount: number;
  kind: 'seatType' | 'unassigned' | 'aggregate';
  /** seatType レーンのとき、通常卓の本数（これ以上の track はオーバーブック）。卓ラベル生成にも使う。 */
  unitCount?: number;
}

const TimelineBoard: React.FC<TimelineBoardProps> = ({ timeline, onEntryClick }) => {
  const { rangeStart, rangeEnd, lanes, hourTicks, overnight } = useMemo(() => {
    // 占有バーは REQUESTED/CONFIRMED のみが枠を占有するが、当日運用では来店済み等も見えたほうが
    // 便利なため終端系も薄い色で表示する。
    const base = timeline.date;
    // 深夜営業（営業終了が翌日）の営業日は、翌0:00以降の予約を 24:00〜 に連続表示する。
    const overnight = isOvernightBusinessDay(timeline.hours);
    // 軸の end 側だけ予約に合わせて後方拡張する（深夜帯を24:00超で表示するため）。
    // 軸の start は営業開始時刻に固定し、前営業日帰属の早い時刻エントリがあっても営業開始より前へは伸ばさない（タスク5）。
    const entryEndMinutes = timeline.entries.map((e) => dateTimeToMinutes(e.end, base, overnight));
    const openMinutes = timeline.hours.map((h) => toMinutes(h.open));
    // 深夜営業日は close が open 以下（翌日）なので +1440 して営業日基準の連続値にする。
    const closeMinutes = timeline.hours.map((h) => {
      const close = toMinutes(h.close);
      return overnight && close <= toMinutes(h.open) ? close + 24 * 60 : close;
    });
    // 営業時間が無い日は 9:00-22:00 をデフォルト表示範囲にする
    let start = openMinutes.length > 0 ? Math.min(...openMinutes) : 9 * 60;
    let end = closeMinutes.length > 0 ? Math.max(...closeMinutes) : 22 * 60;
    // 軸開始は常に営業開始時刻（後方拡張しない）。end 側のみ予約終了に合わせて拡張する。
    if (entryEndMinutes.length > 0) {
      end = Math.max(end, ...entryEndMinutes);
    }
    // 1時間単位に丸めて余白を作る。
    // 通常営業日は従来どおり 24:00 で頭打ち。深夜営業日は 24:00 超（例27:00）まで伸ばす。
    start = Math.floor(start / 60) * 60;
    const endCap = overnight ? 30 * 60 : 24 * 60;
    end = Math.min(Math.ceil(end / 60) * 60, endCap);
    if (end - start < 60) end = start + 60;

    // 席タイプごとに「卓数ぶんの高さを持つ1コンテナ」を作る（E6・タスク3）。
    // 「4名席」unitCount=2 → 高さ2卓ぶんのレーン。卓1/卓2の区切り線・卓ラベルはコンテナ内部に敷く。
    // 連結(span>1)予約は複数卓行にまたがる1本の帯として絶対配置で描く（重複描画しない）。
    // unitCount を超えた track はオーバーブック行として続けて出す。
    const seatTypeLanes: Lane[] = [];
    [...timeline.seatTypes]
      .sort((a, b) => a.sortIndex - b.sortIndex)
      .forEach((st) => {
        // 卓数(unitCount)ぶんのトラックを最初に確保し、時間帯が被らない予約が卓を再利用できるようにする。
        // これで連結予約が容量内なら架空のオーバーブック行を作らない。
        const placed = assignTracks(
          timeline.entries.filter((e) => e.seatTypeId === st.id),
          base,
          st.unitCount,
          overnight,
        );
        const usedTracks = placed.reduce((max, p) => Math.max(max, p.track + p.span), 0);
        // 少なくとも unitCount 本の卓行を出す（空の卓も在庫として見せる）。超過分はオーバーブック行。
        const rowCount = Math.max(st.unitCount, usedTracks, 1);
        seatTypeLanes.push({
          key: st.id,
          label: st.name,
          sublabel: seatCapacitySummary(st),
          placed,
          rowCount,
          kind: 'seatType',
          unitCount: st.unitCount,
        });
      });

    const unassignedEntries = timeline.entries.filter(
      (e) => e.seatTypeId === null || !timeline.seatTypes.some((st) => st.id === e.seatTypeId),
    );
    const unassignedPlaced = assignTracks(unassignedEntries, base, 0, overnight);
    const isAggregate = timeline.seatTypes.length === 0;
    const laneList: Lane[] = [...seatTypeLanes];
    // AGGREGATE 店舗は全件をひとつのレーンで表示。SEAT_TYPE 店舗は未割当がある時のみレーンを出す（従来どおり1行 stacking）
    if (isAggregate || unassignedEntries.length > 0) {
      laneList.push({
        key: '__unassigned__',
        label: isAggregate ? '予約' : '未割当',
        sublabel: null,
        placed: unassignedPlaced,
        rowCount: Math.max(
          unassignedPlaced.reduce((max, p) => Math.max(max, p.track + p.span), 0),
          1,
        ),
        kind: isAggregate ? 'aggregate' : 'unassigned',
      });
    }

    const ticks: number[] = [];
    for (let t = start; t <= end; t += 60) ticks.push(t);

    return { rangeStart: start, rangeEnd: end, lanes: laneList, hourTicks: ticks, overnight };
  }, [timeline]);

  const total = rangeEnd - rangeStart;
  const toPercent = (minutes: number) =>
    Math.max(0, Math.min(100, ((minutes - rangeStart) / total) * 100));

  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720 }}>
        {/* 時間軸ヘッダ */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, p: 1 }}>
            <Typography variant="caption" color="text.secondary">
              席タイプ
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', flexGrow: 1, height: 28 }}>
            {hourTicks.map((t) => (
              <Typography
                key={t}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  left: `${toPercent(t)}%`,
                  transform: 'translateX(-50%)',
                  top: 4,
                }}
              >
                {tickLabel(t)}
              </Typography>
            ))}
          </Box>
        </Box>

        {lanes.length === 0 || timeline.entries.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            この日の予約はありません
          </Typography>
        ) : (
          lanes.map((lane) => {
            const laneHeight = lane.rowCount * ROW_HEIGHT;
            // seatType レーンは卓(unit)ごとに行を分けて区切り線・卓ラベルを敷く。
            // それ以外（未割当/AGGREGATE）は行分割せず、従来どおり1コンテナに track stacking する。
            const isSeatType = lane.kind === 'seatType';
            const unitCount = lane.unitCount ?? 0;
            return (
              <Box
                key={lane.key}
                sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}
              >
                {/* 行ヘッダ: 席タイプ名・容量サブラベル＋卓ラベル（卓1..N）。レーン本体と同じ高さで卓位置を揃える。 */}
                <Box
                  sx={{
                    width: LABEL_WIDTH,
                    flexShrink: 0,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    height: laneHeight,
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {lane.label}
                    </Typography>
                    {lane.sublabel && (
                      <Typography variant="caption" color="text.secondary">
                        {lane.sublabel}
                      </Typography>
                    )}
                  </Box>
                  {/* 卓ラベルは各卓行の右下端に控えめに出す（卓数超過は赤字） */}
                  {isSeatType &&
                    Array.from({ length: lane.rowCount }).map((_, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          right: 6,
                          top: i * ROW_HEIGHT + ROW_HEIGHT - 16,
                          color: i >= unitCount ? 'error.main' : 'text.disabled',
                          lineHeight: 1,
                        }}
                      >
                        卓{i + 1}
                      </Typography>
                    ))}
                </Box>
                {/* レーン本体: 高さ = rowCount×ROW_HEIGHT。背景に卓行の区切り＋時間グリッドを1回だけ敷き、
                    予約バーはその上に絶対配置する（連結は複数卓行にまたがる1本の帯・タスク3/4）。 */}
                <Box
                  sx={{
                    position: 'relative',
                    flexGrow: 1,
                    height: laneHeight,
                  }}
                >
                  {/* 卓行の背景（オーバーブック行は赤系）＋区切り線。seatType レーンのみ。 */}
                  {isSeatType &&
                    Array.from({ length: lane.rowCount }).map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: i * ROW_HEIGHT,
                          height: ROW_HEIGHT,
                          ...(i < lane.rowCount - 1 && {
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }),
                          // unitCount を超えた卓はオーバーブック行として赤系背景
                          ...(i >= unitCount && {
                            backgroundColor: 'rgba(244, 67, 54, 0.06)',
                          }),
                        }}
                      />
                    ))}
                  {/* 時間の目盛線（毎時の点線）。全高を貫く共通背景レイヤーに1回だけ描く。
                      色は視認できる濃さにし、バーの背後に置く（バーで隠れない・タスク4）。 */}
                  {hourTicks.map((t) => (
                    <Box
                      key={t}
                      sx={{
                        position: 'absolute',
                        left: `${toPercent(t)}%`,
                        top: 0,
                        bottom: 0,
                        borderLeft: '1px dashed',
                        borderColor: 'rgba(0, 0, 0, 0.28)',
                        pointerEvents: 'none',
                      }}
                    />
                  ))}
                  {lane.placed.map(({ entry, track, span }) => {
                    const startPct = toPercent(dateTimeToMinutes(entry.start, timeline.date, overnight));
                    const endPct = toPercent(dateTimeToMinutes(entry.end, timeline.date, overnight));
                    const color = barColor(entry.status);
                    // 連結(span>1)は複数卓行にまたがる1本の帯として絶対配置する（重複描画しない・タスク3）。
                    // top=track×ROW_HEIGHT / height=span×ROW_HEIGHT。上下に4pxずつ余白を取り、卓行の区切りやグリッドが見えるようにする。
                    const overbooked = isSeatType && track + span > unitCount;
                    const unitsNote = span > 1 ? `・${span}卓連結` : '';
                    return (
                      <Tooltip
                        key={entry.reservationId}
                        title={`${entry.guestName} ${entry.partySize}名${unitsNote} / ${entry.start.substring(11, 16)}〜${entry.end.substring(11, 16)} / ${RESERVATION_STATUS_LABEL[entry.status]}${overbooked ? '（卓数超過）' : ''}`}
                      >
                        <Box
                          onClick={() => onEntryClick(entry.reservationId)}
                          sx={{
                            position: 'absolute',
                            left: `${startPct}%`,
                            width: `${Math.max(endPct - startPct, 2)}%`,
                            top: track * ROW_HEIGHT + 4,
                            height: span * ROW_HEIGHT - 8,
                            backgroundColor: color.bg,
                            color: color.fg,
                            borderRadius: 1,
                            border: overbooked ? '2px solid #f44336' : 'none',
                            px: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            zIndex: 1,
                            '&:hover': { opacity: 0.85 },
                          }}
                        >
                          {entry.guestName} {entry.partySize}名
                          {span > 1 && (
                            <Box
                              component="span"
                              sx={{
                                px: 0.5,
                                borderRadius: 0.5,
                                backgroundColor: 'rgba(255,255,255,0.28)',
                                fontSize: '0.7rem',
                              }}
                            >
                              {span}卓
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })
        )}

        {/* 凡例 */}
        <Box sx={{ display: 'flex', gap: 2, p: 1, flexWrap: 'wrap' }}>
          {(['REQUESTED', 'CONFIRMED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'] as ReservationStatus[]).map(
            (s) => (
              <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    backgroundColor: barColor(s).bg,
                  }}
                />
                <Typography variant="caption">{RESERVATION_STATUS_LABEL[s]}</Typography>
              </Box>
            ),
          )}
          {/* 通常の卓と、卓数(在庫)を超えたオーバーブック行の見分け方を示す */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                border: '2px solid #f44336',
                backgroundColor: 'rgba(244, 67, 54, 0.06)',
              }}
            />
            <Typography variant="caption">卓数超過（在庫を超えた予約）</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              component="span"
              sx={{
                px: 0.5,
                borderRadius: 0.5,
                backgroundColor: 'rgba(0,0,0,0.12)',
                fontSize: '0.7rem',
              }}
            >
              2卓
            </Box>
            <Typography variant="caption">複数卓を連結した予約</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default TimelineBoard;
